import { useEffect, useState } from 'react';
import { FilePlus2, Trash2, Clock, Flag, CalendarClock, UserCheck, Eye } from 'lucide-react';
import { api } from '../lib/api';
import type { RequestItem, RequestStatus } from '../types';
import { formatDate, formatDateTime } from '../lib/format';
import { Spinner, EmptyState, StatusBadge, ConfirmDialog, inputClass } from './ui';
import { useAuth } from '../lib/auth';
import RequestFormModal from './RequestFormModal';
import RequestDetailModal from './RequestDetailModal';

const priorityColor: Record<string, string> = {
  low: 'text-green-600',
  medium: 'text-yellow-600',
  high: 'text-red-600',
};

export default function Requests() {
  const { user } = useAuth();
  const [requests, setRequests] = useState<RequestItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [selected, setSelected] = useState<RequestItem | null>(null);

  const isRecipient = !!user && ['admin', 'oversight', 'manager'].includes(user.role);
  const canManage = isRecipient;
  const canCreate = !!user && ['manager', 'staff'].includes(user.role);
  const canDelete = !!user && ['admin', 'oversight'].includes(user.role);

  const load = () => {
    setLoading(true);
    api
      .get<RequestItem[]>('/requests')
      .then(setRequests)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const updateStatus = async (id: number, status: RequestStatus) => {
    try {
      const updated = await api.patch<RequestItem>(`/requests/${id}`, { status });
      setRequests((prev) => prev.map((r) => (r.id === id ? updated : r)));
      setSelected((cur) => (cur && cur.id === id ? updated : cur));
    } catch {
      // ignore
    }
  };

  const openRequest = (r: RequestItem) => {
    setSelected(r);
    if (isRecipient && !r.read_by_name && !r.read_at) {
      api
        .post<RequestItem>(`/requests/${r.id}/read`, {})
        .then((updated) => {
          setRequests((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
          setSelected((cur) => (cur && cur.id === updated.id ? updated : cur));
        })
        .catch(() => {});
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/requests/${id}`);
      setDeleteId(null);
      load();
    } catch {
      setDeleteId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <h1 className="text-2xl font-bold">Requests</h1>
        {canCreate && (
          <button onClick={() => setModalOpen(true)} className="px-4 py-2 bg-primary text-black rounded-lg flex items-center space-x-2 hover:bg-primary-dark text-sm">
            <FilePlus2 className="h-4 w-4" /> New Request
          </button>
        )}
      </div>

      {loading ? (
        <Spinner label="Loading requests..." />
      ) : requests.length === 0 ? (
        <EmptyState message="No requests yet." />
      ) : (
        <div className="grid gap-4">
          {requests.map((r) => (
            <div
              key={r.id}
              role="button"
              tabIndex={0}
              onClick={() => openRequest(r)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openRequest(r); }}
              className="bg-white p-5 rounded-lg shadow-lg cursor-pointer hover:shadow-xl hover:ring-1 hover:ring-primary/40 transition focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <StatusBadge status={r.status} />
                  <span className={`inline-flex items-center text-xs font-medium ${priorityColor[r.priority]}`}>
                    <Flag className="h-3.5 w-3.5 mr-1" /> {r.priority}
                  </span>
                  <span className="text-sm text-gray-500">{r.user_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  {canManage && (
                    <select
                      className={`${inputClass} !w-auto !py-1.5`}
                      value={r.status}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => updateStatus(r.id, e.target.value as RequestStatus)}
                    >
                      {(['open', 'in_progress', 'resolved', 'dismissed'] as RequestStatus[]).map((s) => (
                        <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  )}
                  {canDelete && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteId(r.id); }}
                      className="p-2 rounded-lg hover:bg-red-50 text-red-500"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              <h3 className="font-semibold text-gray-800 mb-1">{r.title}</h3>
              {r.description && <p className="text-sm text-gray-600 whitespace-pre-line mb-3 line-clamp-2">{r.description}</p>}
              <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400">
                <span className="inline-flex items-center">
                  <Clock className="h-3.5 w-3.5 mr-1" />
                  Created {formatDateTime(r.created_at)}
                </span>
                {r.due_date && (
                  <span className="inline-flex items-center text-amber-600">
                    <CalendarClock className="h-3.5 w-3.5 mr-1" /> Due {formatDate(r.due_date)}
                  </span>
                )}
                {r.responded_by_name && (
                  <span className="inline-flex items-center text-green-600">
                    <UserCheck className="h-3.5 w-3.5 mr-1" /> Responded by {r.responded_by_name}
                  </span>
                )}
                {r.read_by_name && (
                  <span className="inline-flex items-center text-primary-dark">
                    <Eye className="h-3.5 w-3.5 mr-1" /> Read by {r.read_by_name}
                  </span>
                )}
                <span className="text-gray-400">Click to open</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <RequestFormModal open={modalOpen} onClose={() => setModalOpen(false)} onSubmitted={load} />

      <RequestDetailModal
        request={selected}
        canManage={canManage}
        onClose={() => setSelected(null)}
        onStatusChange={updateStatus}
      />

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && handleDelete(deleteId)}
        title="Delete Request"
        message="Are you sure you want to delete this request?"
      />
    </div>
  );
}
